import _ from 'lodash'
import Geohash from 'ngeohash'
import { useEffect, useMemo, useRef, useState } from 'react'

import * as MapModel from '@/components/map/components/model'
import type { MapViewProps } from '@/components/map/components/view'

import type { PoiType } from '@/api/dto/poi'
import Shared from '@/shared'
import useHash from '@/shared/hooks/useHash'
import type { Dispatch, SetStateAction } from 'react'

import { SALE_TYPE } from '@/enums/detail'
import { HASH_KEY_TYPE } from '@/enums/map'

import { fetchGetPois } from '@/api/client/map'
import { ClientRequestError } from '@/lib/axiosClient'

export interface CachedPins {
  [key: string]: { pins: MapModel.Pin[]; timestamp: number }
}

export interface LatestPins {
  [key: string]: MapModel.Pin[]
}

export interface MapViewModel {
  texts: {
    errorOnDataLoadText: string
  }
  states: {
    location: {
      default: { lat: number; lng: number }
    }
    pins: { [key: string]: MapModel.Pin[] }
    isErrorOnCurrPos: boolean
    isErrorOnDataLoad: boolean
    isFilterOn: boolean
    zoomLevel: number
    errorOnCurrPosText: string
  }
  setters: {
    setLocation: Dispatch<
      SetStateAction<{
        default: { lat: number; lng: number }
      }>
    >
    setIsErrorOnCurrPos: Dispatch<SetStateAction<boolean>>
    setIsFilterOn: Dispatch<SetStateAction<boolean>>
    setZoomLevel: Dispatch<SetStateAction<number>>
  }
  handlers: object
}

const useMapViewModel = (props: MapViewProps): MapViewModel => {
  const gwanghwamunLocation = {
    lat: 37.569655,
    lng: 126.977043
  }
  const defaultLocation = {
    lat: gwanghwamunLocation.lat,
    lng: gwanghwamunLocation.lng
  }
  if (props.type && props.id && props.lat != null && props.lng != null) {
    defaultLocation.lat = props.lat
    defaultLocation.lng = props.lng
  } else if (props.searchLat && props.searchLng) {
    defaultLocation.lat = Number(props.searchLat)
    defaultLocation.lng = Number(props.searchLng)
  }
  const { getHashByKey } = useHash()
  const isMountRef = useRef(true)
  const errorOnDataLoadText = `데이터를 요청하는 도중 문제가 발생했습니다.\n잠시 후 다시 시도해주세요`
  const [errorOnCurrPosText, setErrorOnCurrPosText] = useState('')
  const [isErrorOnCurrPos, setIsErrorOnCurrPos] = useState(false)
  const [isErrorOnDataLoad, setIsErrorOnDataLoad] = useState(false)
  const [location, setLocation] = useState({
    default: defaultLocation
  })
  const cachedPins = useRef<CachedPins>({})
  const setGlobalCachedPins = Shared.States.useGlobalCachedPins((state) => state.setCachedPins)
  const [latestPins, setLatestPins] = useState<LatestPins>({})
  const [isFilterOn, setIsFilterOn] = useState(getHashByKey(HASH_KEY_TYPE.IS_SALE) === SALE_TYPE.ON)
  const [zoomLevel, setZoomLevel] = useState<MapViewModel['states']['zoomLevel']>(
    getHashByKey(HASH_KEY_TYPE.ZOOM) && !isNaN(Number(getHashByKey(HASH_KEY_TYPE.ZOOM)))
      ? Number(getHashByKey(HASH_KEY_TYPE.ZOOM))
      : 16
  )

  const currentGeohashListStringBounds = useMemo(() => {
    const code = Geohash.encode(location.default.lat, location.default.lng, 6)
    const codeNeighbors = Geohash.neighbors(code)
    const top = Geohash.neighbors(codeNeighbors[0])
    const bottom = Geohash.neighbors(codeNeighbors[4])

    const topNeighbor = [top[0], top[1], top[7]]
    const bottomNeighbor = [bottom[4], bottom[3], bottom[5]]

    return [...codeNeighbors, code, ...topNeighbor, ...bottomNeighbor].join()
  }, [location.default.lat, location.default.lng])

  const makePins = (data: PoiType[]): CachedPins => {
    const currentTime = Date.now()
    return data.reduce((previousValue, currentValue) => {
      return {
        ...previousValue,
        [currentValue.geohash]: {
          timestamp: currentTime,
          pins: [
            ...currentValue.parkinglots.filter((p) => !p.isAirport).map((parkinglot) => new MapModel.Pin(parkinglot)),
            ...currentValue.sharedParkinglots.map((parkinglot) => new MapModel.Pin(parkinglot)),
            ...currentValue.evStations.map((parkinglot) => new MapModel.Pin(parkinglot))
          ]
        }
      }
    }, {})
  }

  const validateGeohashDomestic = (geohash: string[]) => {
    const IN_LAND_ZONE = 'wy' // NOTE: 내륙지역 geohash zone
    const JEJU_ZONE = 'wv' // NOTE: 제주도 geohash zone

    const isDomestic = geohash.every((geo) => geo.startsWith(IN_LAND_ZONE) || geo.startsWith(JEJU_ZONE))

    return isDomestic
  }

  const handleGetPois = async (targetGeohashes: string[], cacheTime?: number) => {
    const ONE_MINUTE_IN_MILLISECONDS = cacheTime == null ? 1000 * 60 : cacheTime
    const currentTime = Date.now()

    if (!validateGeohashDomestic(targetGeohashes)) throw new Shared.Errors.GeohashValidationError()
    if (zoomLevel <= 15) return

    const expired = _.filter(targetGeohashes, (geohash) => {
      return (
        !cachedPins.current[geohash] || currentTime - cachedPins.current[geohash].timestamp > ONE_MINUTE_IN_MILLISECONDS
      )
    })

    if (expired.length === 0) return

    const { data: pois } = await fetchGetPois({
      geohash: expired.join(','),
      shareMode: true,
      partnerMode: true
    })

    cachedPins.current = { ...cachedPins.current, ...makePins(pois ?? []) }

    const latestPins = _.reduce(
      targetGeohashes,
      (prev, hash) => {
        if (hash in cachedPins.current) {
          prev[hash] = cachedPins.current[hash].pins
        }
        return prev
      },
      {} as LatestPins
    )

    setLatestPins({ ...latestPins })
    setGlobalCachedPins(cachedPins.current)
  }

  useEffect(() => {
    const getCurrentLocationWithAuthorization = async () => {
      try {
        const { lat, lng } = await Shared.Utils.getCurrentLatLng()
        if (location.default.lng === gwanghwamunLocation.lng && location.default.lat === gwanghwamunLocation.lat) {
          setLocation((prev) => ({ ...prev, default: { lat, lng } }))
        }
      } catch (err: unknown) {
        if (err instanceof Shared.Errors.GeolocationError) {
          setIsErrorOnCurrPos(true)
          setErrorOnCurrPosText(err.message)
        } else {
          throw err
        }
      }
    }

    getCurrentLocationWithAuthorization()
  }, [])

  useEffect(() => {
    const exec = async () => {
      try {
        await handleGetPois(currentGeohashListStringBounds.split(','))
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          setIsErrorOnDataLoad(true)
          return
        } else if (err instanceof Shared.Errors.GeohashValidationError) {
          setIsErrorOnDataLoad(true)
          return
        } else {
          throw err
        }
      }
    }

    exec()
  }, [location.default.lat, location.default.lng])

  useEffect(() => {
    const exec = async () => {
      try {
        if (!isMountRef.current) {
          await handleGetPois(currentGeohashListStringBounds.split(','), 0)
        } else {
          isMountRef.current = false
        }
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          setIsErrorOnDataLoad(true)
          return
        } else if (err instanceof Shared.Errors.GeohashValidationError) {
          setIsErrorOnDataLoad(true)
          return
        } else {
          throw err
        }
      }
    }

    exec()
  }, [zoomLevel, isFilterOn])

  useEffect(() => {
    if (props.searchLat && props.searchLng) {
      const location = {
        lat: Number(props.searchLat),
        lng: Number(props.searchLng)
      }
      setLocation({ default: location })
    }
  }, [props.searchLat, props.searchLng])

  return {
    texts: {
      errorOnDataLoadText
    },
    states: {
      location,
      pins: latestPins,
      isErrorOnCurrPos,
      isErrorOnDataLoad,
      isFilterOn,
      zoomLevel,
      errorOnCurrPosText
    },
    setters: {
      setIsErrorOnCurrPos,
      setLocation,
      setIsFilterOn,
      setZoomLevel
    },
    handlers: {}
  }
}

export default useMapViewModel
